import { create } from "zustand";
import { getError } from "../../utils/getError";
import api, { isAbortError } from "../../utils/api";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";
import type { TrackingUser } from "../../Layouts/admin/components/sections/trackingLinks/types";

export interface AccessHistoryEntry {
  ip: string;
  accessCount: number;
  firstAccess: any;
  lastAccess: any;
  userAgent: string;
}

export interface Status {
  id: string;
  name: string;
  color?: string;
  icon?: string;
}

export interface TrackingLink {
  id: string;
  name: string;
  internalNotes?: string;
  units: string[];
  shareTrips?: boolean;
  accessType: "public" | "protected";
  hasAccessCode: boolean;
  activationType: "immediate" | "scheduled" | "inactive";
  activationDate: any;
  durationValue?: number;
  durationUnit?: "hours" | "days" | "weeks" | "months";
  expirationDate: any;
  status_id: string;
  status: Status | null;
  uniqueHash: string;
  accessCount: number;
  company_id: string;
  publicUrl: string;
  unitsCount: number;
  accessHistory?: AccessHistoryEntry[];
  createdAt?: any;
  updatedAt?: any;
}

export interface CreateTrackingLinkData {
  name: string;
  internalNotes?: string;
  units: string[];
  shareTrips?: boolean;
  trackingUsers?: TrackingUser[];
  accessType: "public" | "protected";
  accessCode?: string;
  activationType: "immediate" | "scheduled" | "inactive";
  activationDate?: string;
  durationValue?: number;
  durationUnit?: "hours" | "days" | "weeks" | "months";
  company_id: string;
}

export interface UpdateTrackingLinkData {
  name?: string;
  internalNotes?: string;
  units?: string[];
  shareTrips?: boolean;
  trackingUsers?: TrackingUser[];
  accessType?: "public" | "protected";
  accessCode?: string;
  status_id?: string;
  durationValue?: number;
  durationUnit?: "hours" | "days" | "weeks" | "months";
}

interface TrackingLinksStore {
  trackingLinks: TrackingLink[];
  loading: boolean;
  error: string | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  fetchTrackingLinks: (params?: {
    limit?: number;
    page?: number;
    accessType?: string;
    status?: string;
    search?: string;
  }) => Promise<void>;
  createTrackingLink: (
    data: CreateTrackingLinkData,
  ) => Promise<TrackingLink | null>;
  updateTrackingLink: (
    id: string,
    data: UpdateTrackingLinkData,
  ) => Promise<TrackingLink | null>;
  deleteTrackingLink: (id: string) => Promise<boolean>;
  removeTrackingLink: (id: string) => Promise<boolean>;
  extendTrackingLink: (
    trackingLinkId: string,
    durationValue: number,
    durationUnit: string,
  ) => Promise<boolean>;
  getTrackingLinkDetails: (trackingLinkId: string) => Promise<TrackingLink>;
  updateTrackingLinkInStore: (
    trackingLinkId: string,
    updatedTrackingLink: Partial<TrackingLink>,
  ) => void;
  addTrackingLinkToStore: (trackingLink: TrackingLink) => void;
  removeTrackingLinkFromStore: (trackingLinkId: string) => void;
}

export const useTrackingLinksStore = create<TrackingLinksStore>((set, get) => ({
  trackingLinks: [],
  loading: false,
  error: null,
  meta: undefined,

  fetchTrackingLinks: async (params = {}) => {
    if (get().loading) {
      return;
    }
    const fetchGen = getAdminFetchGeneration();

    set({ loading: true, error: null });
    try {
      const queryParams: Record<string, any> = {
        limit: params.limit ?? 6,
        page: params.page ?? 1,
      };

      if (params.accessType && params.accessType !== "all") {
        queryParams.accessType = params.accessType;
      }
      if (params.status && params.status !== "all") {
        queryParams.status = params.status;
      }
      if (params.search) {
        queryParams.search = params.search;
      }

      const response = await api.get("/trackingLinks", { params: queryParams });

      if (getAdminFetchGeneration() !== fetchGen) return;
      set({
        trackingLinks: response.data.data || [],
        meta: response.data.meta,
        loading: false,
        error: null,
      });
    } catch (error) {
      if (getAdminFetchGeneration() !== fetchGen || isAbortError(error)) return;
      console.error("Error fetching tracking links:", error);
      set({
        trackingLinks: [],
        loading: false,
        error: getError(error),
      });
    }
  },

  createTrackingLink: async (data) => {
    try {
      const response = await api.post("/trackingLinks", data);
      const newLink = response.data.data;
      if (newLink) {
        get().addTrackingLinkToStore(newLink);
      }
      return newLink;
    } catch (error) {
      console.error("Error creating tracking link:", error);
      return null;
    }
  },

  updateTrackingLink: async (id, data) => {
    try {
      const response = await api.put(`/trackingLinks?id=${id}`, data);
      const updatedLink = response.data.data;
      if (updatedLink) {
        get().updateTrackingLinkInStore(id, updatedLink);
      }
      return updatedLink;
    } catch (error) {
      console.error("Error updating tracking link:", error);
      return null;
    }
  },

  deleteTrackingLink: async (id) => {
    try {
      await api.delete(`/trackingLinks?id=${id}`);
      get().removeTrackingLinkFromStore(id);
      return true;
    } catch (error) {
      console.error("Error deleting tracking link:", error);
      return false;
    }
  },

  removeTrackingLink: async (id) => {
    try {
      await api.delete(`/trackingLinks?id=${id}&permanent=true`);
      get().removeTrackingLinkFromStore(id);
      return true;
    } catch (error) {
      console.error("Error removing tracking link:", error);
      return false;
    }
  },

  updateTrackingLinkInStore: (trackingLinkId, updatedTrackingLink) => {
    set((state) => ({
      trackingLinks: state.trackingLinks.map((trackingLink) =>
        trackingLink.id === trackingLinkId
          ? { ...trackingLink, ...updatedTrackingLink }
          : trackingLink,
      ),
    }));
  },

  addTrackingLinkToStore: (trackingLink) => {
    set((state) => ({
      trackingLinks: [trackingLink, ...state.trackingLinks],
    }));
  },

  removeTrackingLinkFromStore: (trackingLinkId) => {
    set((state) => ({
      trackingLinks: state.trackingLinks.filter(
        (trackingLink) => trackingLink.id !== trackingLinkId,
      ),
    }));
  },

  extendTrackingLink: async (trackingLinkId: string, durationValue: number, durationUnit: string) => {
    try {
      const response = await api.put(`/trackingLinks/extend?id=${trackingLinkId}`, {
        durationValue,
        durationUnit,
      });
      
      const updatedLink = response.data.data;
      if (updatedLink) {
        // Actualizar el link en el store
        set((state) => ({
          trackingLinks: state.trackingLinks.map((link) =>
            link.id === trackingLinkId ? updatedLink : link,
          ),
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error extending tracking link:", error);
      throw error;
    }
  },

  getTrackingLinkDetails: async (trackingLinkId: string) => {
    try {
      const response = await api.get(`/trackingLinks/details?id=${trackingLinkId}`);
      return response.data.data.data;
    } catch (error) {
      console.error("Error fetching tracking link details:", error);
      throw error;
    }
  },
}));
